<template>
  <div class="report-page">
    <!-- Header -->
    <div class="report-header d-flex align-center justify-space-between px-3 px-sm-6 py-3">
      <div class="d-flex align-center" style="gap: 12px;">
        <v-btn icon @click="$router.push({ name: 'auth', params: { authPage: 'shop' } })">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <h2 class="mb-0 primary--text font-weight-bold" style="font-size: 20px;">
          <v-icon color="primary" class="mr-1" style="vertical-align: middle;">mdi-chart-bar</v-icon>
          Add Spent Report
        </h2>
      </div>
      <span class="grey--text text--darken-1 d-none d-sm-inline" style="font-size: 13px;">Accounting Dashboard</span>
    </div>

    <v-divider />

    <!-- Body -->
    <div class="report-body pa-3 pa-sm-5">
      <v-row no-gutters style="gap: 0; align-items: flex-start;">
        <!-- ===== LEFT: Monthly Summary ===== -->
        <v-col cols="12" class="mb-5" order="1">
          <v-card rounded="lg" class="pa-4" elevation="1">
            <div class="d-flex align-center justify-space-between mb-4 flex-wrap" style="gap: 12px;">
              <h3 class="primary--text font-weight-bold mb-0 d-flex align-center" style="font-size: 15px;">
                <v-icon color="primary" small class="mr-1">mdi-calendar-month</v-icon>
                Monthly Summary
              </h3>
              <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                <!-- Period label -->
                <div class="px-3 py-1 rounded-lg" style="background: var(--v-primary-lighten5, #e8eaff);">
                  <span class="primary--text font-weight-medium" style="font-size: 13px;">
                    {{ periodLabel }}
                  </span>
                </div>
                <!-- Month picker -->
                <v-menu
                  ref="monthMenu"
                  v-model="monthMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      small
                      outlined
                      color="primary"
                      style="min-width: 130px; text-transform: none;"
                    >
                      <v-icon small left>mdi-calendar</v-icon>
                      {{ monthLabel }}
                    </v-btn>
                  </template>
                  <v-date-picker
                    v-model="selectedMonth"
                    type="month"
                    no-title
                    scrollable
                    @input="
                      monthMenu = false;
                      fetchMonthlySummary();
                    "
                  />
                </v-menu>
              </div>
            </div>

            <!-- Metric cards -->
            <div class="metric-grid">
              <div class="metric-card metric-rev">
                <div class="metric-label">Revenue</div>
                <div class="metric-value green--text text--darken-2">
                  {{ isLoadingMonthly ? '—' : formatCurrency(summary.rev) }}
                </div>
                <v-progress-linear v-if="isLoadingMonthly" indeterminate color="green" height="2" class="mt-1" />
              </div>

              <div class="metric-card metric-spent">
                <div class="metric-label">Ad Spent</div>
                <div class="metric-value red--text text--darken-1">
                  {{ isLoadingMonthly ? '—' : formatCurrency(summary.spent) }}
                </div>
                <v-progress-linear v-if="isLoadingMonthly" indeterminate color="red" height="2" class="mt-1" />
              </div>

              <div class="metric-card metric-sales">
                <div class="metric-label">Total Sales</div>
                <div class="metric-value blue--text text--darken-2">
                  {{ isLoadingMonthly ? '—' : summary.sales.toLocaleString() }}
                  <span style="font-size: 13px; font-weight: 400;" class="grey--text ml-1">orders</span>
                </div>
                <v-progress-linear v-if="isLoadingMonthly" indeterminate color="blue" height="2" class="mt-1" />
              </div>

              <div class="metric-card" :class="roasClass">
                <div class="metric-label">ROAS</div>
                <div class="metric-value" :class="roasColorClass">
                  {{ isLoadingMonthly ? '—' : summary.roas.toFixed(2) }}x
                </div>
                <v-progress-linear v-if="isLoadingMonthly" indeterminate color="orange" height="2" class="mt-1" />
              </div>

              <!-- Profit (tạm ẩn)
              <div class="metric-card" style="border-color: #7c4dff; background: #f3f0ff;">
                <div class="metric-label">Profit (Rev - Spent)</div>
                <div class="metric-value purple--text text--darken-2">
                  {{ isLoadingMonthly ? '—' : formatCurrency(summary.rev - summary.spent) }}
                </div>
                <v-progress-linear v-if="isLoadingMonthly" indeterminate color="purple" height="2" class="mt-1" />
              </div>
              -->
            </div>

            <!-- Note -->
            <!-- <div class="mt-4 px-3 py-2 rounded-lg grey lighten-4 text-center">
              <span class="grey--text text--darken-2" style="font-size: 12px;">
                <v-icon small class="mr-1">mdi-information-outline</v-icon>
                Data updates every day at 00:00 UTC
              </span>
            </div> -->
          </v-card>
        </v-col>

        <!-- ===== BOTTOM RIGHT: Images/Video Ads Content Report ===== -->
        <v-col cols="12" md="6" class="pl-md-3" order="3">
          <!-- ===== Share Prompt List ===== -->
          <v-card rounded="lg" class="pa-4" elevation="1">
            <!-- Header + date filter -->
            <div class="d-flex align-center justify-space-between mb-4" style="gap: 12px; flex-wrap: wrap;">
              <h3 class="primary--text font-weight-bold mb-0 d-flex align-center" style="font-size: 15px;">
                <v-icon color="primary" small class="mr-1">mdi-message-text-outline</v-icon>
                Images/Video Ads Content Report
                <v-chip v-if="sharedPrompt.total" x-small label color="primary" outlined class="ml-2 font-weight-bold">
                  {{ sharedPrompt.total }}
                </v-chip>
              </h3>
              <div class="d-flex align-center filter-row" style="gap: 6px; flex-wrap: wrap;">
                <!-- Start date -->
                <v-menu
                  v-model="spStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      small
                      outlined
                      color="primary"
                      class="date-pill"
                      style="text-transform: none;"
                    >
                      <v-icon small left>mdi-calendar-start</v-icon>
                      {{ formatDisplayDateCompact(sharedPrompt.startDate) }}
                    </v-btn>
                  </template>
                  <v-date-picker
                    v-model="sharedPrompt.startDate"
                    no-title
                    scrollable
                    :max="sharedPrompt.endDate || today"
                    @input="
                      spStartMenu = false;
                      sharedPrompt.page = 1;
                      fetchSharedPrompts();
                    "
                  />
                </v-menu>
                <v-icon small color="grey">mdi-arrow-right</v-icon>
                <!-- End date -->
                <v-menu
                  v-model="spEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      small
                      outlined
                      color="primary"
                      class="date-pill"
                      style="text-transform: none;"
                    >
                      <v-icon small left>mdi-calendar-end</v-icon>
                      {{ formatDisplayDateCompact(sharedPrompt.endDate) }}
                    </v-btn>
                  </template>
                  <v-date-picker
                    v-model="sharedPrompt.endDate"
                    no-title
                    scrollable
                    :min="sharedPrompt.startDate"
                    :max="today"
                    @input="
                      spEndMenu = false;
                      sharedPrompt.page = 1;
                      fetchSharedPrompts();
                    "
                  />
                </v-menu>
                <v-btn
                  depressed
                  small
                  color="primary"
                  class="filter-icon-btn"
                  :loading="sharedPrompt.isLoading"
                  @click="
                    sharedPrompt.page = 1;
                    fetchSharedPrompts();
                  "
                >
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
                <!-- Store picker -->
                <v-menu
                  v-model="storePicker.menu"
                  :close-on-content-click="false"
                  offset-y
                  max-width="320"
                  min-width="320"
                  transition="scale-transition"
                  @input="onStorePickerToggle"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      small
                      depressed
                      color="primary"
                      class="filter-store-btn"
                      style="text-transform: none;"
                    >
                      <v-icon small left>mdi-storefront-outline</v-icon>
                      <span class="text-truncate" style="max-width: 130px;">
                        {{
                          selectedStore
                            ? (selectedStore.storeDetail && selectedStore.storeDetail.name) || 'Store'
                            : 'Choose Store'
                        }}
                      </span>
                      <v-icon small right>mdi-menu-down</v-icon>
                    </v-btn>
                  </template>
                  <v-card class="store-picker-card">
                    <div class="pa-2">
                      <v-text-field
                        v-model="storePicker.search"
                        prepend-inner-icon="mdi-magnify"
                        placeholder="Search stores"
                        hide-details
                        dense
                        outlined
                        autofocus
                      />
                    </div>
                    <v-divider />
                    <div v-if="selectedStore">
                      <v-list-item dense @click="clearSelectedStore">
                        <v-list-item-icon class="mr-2">
                          <v-icon small>mdi-close-circle-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="grey--text text--darken-2" style="font-size: 13px;">
                            Show all (default)
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider />
                    </div>
                    <div v-if="isLoadingStores" class="text-center py-3">
                      <v-progress-circular indeterminate size="20" color="primary" />
                    </div>
                    <v-list v-else dense class="store-picker-list">
                      <v-list-item
                        v-for="store in filteredStores"
                        :key="store.id"
                        :class="{ 'store-picker-item--active': selectedStore && selectedStore.id === store.id }"
                        :disabled="!store.isEnabled"
                        @click="onPickStore(store)"
                      >
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium" style="font-size: 13px;">
                            {{ store.storeDetail && store.storeDetail.name }}
                          </v-list-item-title>
                          <v-list-item-subtitle style="font-size: 11px;">
                            {{ store.storeDetail && store.storeDetail.defaultSystemDomain }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action v-if="selectedStore && selectedStore.id === store.id">
                          <v-icon small color="primary">mdi-check-circle</v-icon>
                        </v-list-item-action>
                      </v-list-item>
                      <div v-if="!filteredStores.length" class="text-center grey--text py-3" style="font-size: 12px;">
                        No stores match
                      </div>
                    </v-list>
                  </v-card>
                </v-menu>
              </div>
            </div>

            <!-- Selected store info bar -->
            <div v-if="selectedStore" class="selected-store-bar d-flex align-center mb-3">
              <div class="store-image mr-2">
                <v-img src="/admin/img/store.png" />
              </div>
              <div class="flex-grow-1" style="min-width: 0;">
                <div class="font-weight-bold primary--text text-truncate" style="font-size: 13px;">
                  {{ selectedStore.storeDetail && selectedStore.storeDetail.name }}
                </div>
                <div class="grey--text text--darken-1 text-truncate" style="font-size: 11px;">
                  {{ selectedStore.storeDetail && selectedStore.storeDetail.defaultSystemDomain }}
                </div>
              </div>
              <v-btn icon small :disabled="isLoadingProducts" @click="loadStoreProducts(true)">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
              <v-btn icon small @click="clearSelectedStore">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>

            <!-- Product search (when a store is picked) -->
            <v-text-field
              v-if="selectedStore"
              v-model="productSearch"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search products by title"
              hide-details
              dense
              outlined
              clearable
              class="mb-3"
              @input="onProductSearch"
            />

            <!-- Table (default: shared prompts) -->
            <div v-if="!selectedStore" class="table-scroll-wrapper">
              <v-data-table
                :headers="sharedPromptHeaders"
                :items="sharedPrompt.list"
                :loading="sharedPrompt.isLoading"
                :items-per-page="-1"
                hide-default-footer
                item-key="_id"
                class="report-table share-prompt-table"
              >
                <!-- Product Name -->
                <template v-slot:[`item.productName`]="{ item }">
                  <v-tooltip top max-width="320">
                    <template v-slot:activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on" class="cell-ellipsis font-weight-medium primary--text">
                        {{ getProductName(item) }}
                      </div>
                    </template>
                    <span>{{ getProductName(item) }}</span>
                  </v-tooltip>
                </template>

                <!-- Attachments -->
                <template v-slot:[`item.resource_input`]="{ item }">
                  <div
                    v-if="item.resource_input && item.resource_input.length"
                    class="d-flex flex-wrap py-1"
                    style="gap: 5px;"
                  >
                    <a
                      v-for="(url, i) in item.resource_input"
                      :key="i"
                      :href="imageHelpers.url(url)"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <video
                        v-if="isVideo(url)"
                        :src="imageHelpers.url(url)"
                        width="36"
                        height="36"
                        class="attachment-thumb"
                        style="object-fit: cover; display: block;"
                      />
                      <v-img
                        v-else
                        :src="imageHelpers.url(url, { width: 60, height: 60 })"
                        width="36"
                        height="36"
                        class="rounded attachment-thumb"
                        contain
                      />
                    </a>
                  </div>
                  <span v-else class="grey--text">—</span>
                </template>

                <!-- Content / Prompt -->
                <template v-slot:[`item.prompt`]="{ item }">
                  <div class="content-cell">
                    <v-tooltip top max-width="380">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="cell-clamp">{{ item.prompt || '—' }}</div>
                      </template>
                      <span style="white-space: pre-wrap;">{{ item.prompt || '—' }}</span>
                    </v-tooltip>
                    <v-tooltip top v-if="item.prompt">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          icon
                          x-small
                          class="copy-inline-btn"
                          @click.stop="copyContent(item.prompt)"
                        >
                          <v-icon size="14" :color="isCopied(item.prompt) ? 'success' : 'grey darken-1'">
                            {{ isCopied(item.prompt) ? 'mdi-check' : 'mdi-content-copy' }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ isCopied(item.prompt) ? 'Copied!' : 'Copy content' }}</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- Created At -->
                <template v-slot:[`item.createdAt`]="{ item }">
                  <span class="created-cell grey--text text--darken-2">{{ formatDate(item.createdAt) }}</span>
                </template>

                <!-- Actions -->
                <template v-slot:[`item.actions`]="{ item }">
                  <div class="row-actions">
                    <v-btn
                      outlined
                      color="primary"
                      class="row-action-btn row-action-btn--detail"
                      @click="openDetailDialog(item)"
                    >
                      <v-icon size="16" left>mdi-eye-outline</v-icon>
                      Detail
                    </v-btn>
                  </div>
                </template>

                <!-- Empty state -->
                <template v-slot:no-data>
                  <div class="text-center py-8 grey--text">
                    <v-icon large color="grey lighten-2">mdi-message-off-outline</v-icon>
                    <div class="mt-2" style="font-size: 13px;">No share prompts in this period</div>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Table (store mode: products of the selected store) -->
            <div v-else class="table-scroll-wrapper">
              <v-data-table
                :headers="storeProductHeaders"
                :items="storeProducts"
                :loading="isLoadingProducts"
                :items-per-page="-1"
                hide-default-footer
                item-key="_id"
                class="report-table share-prompt-table"
              >
                <!-- Product Name -->
                <template v-slot:[`item.title`]="{ item }">
                  <div class="d-flex align-center" style="gap: 8px;">
                    <div class="product-row-thumb">
                      <v-img v-if="getProductImage(item)" :src="getProductImage(item)" :aspect-ratio="1" contain />
                      <v-icon v-else color="grey lighten-1" small>mdi-image-off-outline</v-icon>
                    </div>
                    <v-tooltip top max-width="320">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="cell-ellipsis font-weight-medium primary--text">
                          {{ item.title || 'Untitled' }}
                        </div>
                      </template>
                      <span>{{ item.title || 'Untitled' }}</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- Attachments (from latest share prompt) -->
                <template v-slot:[`item.attachments`]="{ item }">
                  <template v-if="isProductPromptLoading(item)">
                    <v-progress-circular indeterminate size="16" color="primary" />
                  </template>
                  <template v-else>
                    <div
                      v-if="
                        getLatestPrompt(item) &&
                          getLatestPrompt(item).resource_input &&
                          getLatestPrompt(item).resource_input.length
                      "
                      class="d-flex flex-wrap py-1"
                      style="gap: 5px;"
                    >
                      <a
                        v-for="(url, i) in getLatestPrompt(item).resource_input"
                        :key="i"
                        :href="imageHelpers.url(url)"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <video
                          v-if="isVideo(url)"
                          :src="imageHelpers.url(url)"
                          width="36"
                          height="36"
                          class="attachment-thumb"
                          style="object-fit: cover; display: block;"
                        />
                        <v-img
                          v-else
                          :src="imageHelpers.url(url, { width: 60, height: 60 })"
                          width="36"
                          height="36"
                          class="rounded attachment-thumb"
                          contain
                        />
                      </a>
                    </div>
                    <span v-else class="grey--text">—</span>
                  </template>
                </template>

                <!-- Content (from latest share prompt) -->
                <template v-slot:[`item.content`]="{ item }">
                  <template v-if="isProductPromptLoading(item)">
                    <span class="grey--text">…</span>
                  </template>
                  <template v-else>
                    <div v-if="getLatestPrompt(item) && getLatestPrompt(item).prompt" class="content-cell">
                      <v-tooltip top max-width="380">
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on" class="cell-clamp">
                            {{ getLatestPrompt(item).prompt }}
                          </div>
                        </template>
                        <span style="white-space: pre-wrap;">{{ getLatestPrompt(item).prompt }}</span>
                      </v-tooltip>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            x-small
                            class="copy-inline-btn"
                            @click.stop="copyContent(getLatestPrompt(item).prompt)"
                          >
                            <v-icon
                              size="14"
                              :color="isCopied(getLatestPrompt(item).prompt) ? 'success' : 'grey darken-1'"
                            >
                              {{ isCopied(getLatestPrompt(item).prompt) ? 'mdi-check' : 'mdi-content-copy' }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ isCopied(getLatestPrompt(item).prompt) ? 'Copied!' : 'Copy content' }}</span>
                      </v-tooltip>
                    </div>
                    <span v-else class="grey--text">—</span>
                  </template>
                </template>

                <!-- Created At (from latest share prompt) -->
                <template v-slot:[`item.createdAt`]="{ item }">
                  <template v-if="isProductPromptLoading(item)">
                    <span class="grey--text">…</span>
                  </template>
                  <template v-else>
                    <span v-if="getLatestPrompt(item)" class="created-cell grey--text text--darken-2">{{
                      formatDate(getLatestPrompt(item).createdAt)
                    }}</span>
                    <span v-else class="grey--text">—</span>
                  </template>
                </template>

                <!-- Action (Detail + Upload) -->
                <template v-slot:[`item.action`]="{ item }">
                  <div class="row-actions">
                    <v-btn
                      outlined
                      color="primary"
                      class="row-action-btn row-action-btn--detail"
                      :disabled="!getLatestPrompt(item)"
                      @click="openDetailDialog(getLatestPrompt(item), item)"
                    >
                      <v-icon size="16" left>mdi-eye-outline</v-icon>
                      Detail
                    </v-btn>
                    <v-btn depressed color="primary" class="row-action-btn" @click="openProductUploadDialog(item)">
                      <v-icon size="16" left>mdi-cloud-upload-outline</v-icon>
                      Upload
                    </v-btn>
                  </div>
                </template>

                <!-- Empty state -->
                <template v-slot:no-data>
                  <div class="text-center py-8 grey--text">
                    <v-icon large color="grey lighten-2">mdi-package-variant-closed-remove</v-icon>
                    <div class="mt-2" style="font-size: 13px;">
                      No products found{{ productSearch ? ' matching "' + productSearch + '"' : '' }}
                    </div>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Pagination (default) -->
            <div v-if="!selectedStore && sharedPrompt.totalPage > 1" class="d-flex justify-end mt-3">
              <v-pagination
                v-model="sharedPrompt.page"
                :length="sharedPrompt.totalPage"
                :total-visible="$vuetify.breakpoint.xs ? 3 : 5"
                color="primary"
                circle
                @input="fetchSharedPrompts"
              />
            </div>

            <!-- Pagination (store products) -->
            <div v-if="selectedStore && storeProductsTotalPage > 1" class="d-flex justify-end mt-3">
              <v-pagination
                v-model="productPage"
                :length="storeProductsTotalPage"
                :total-visible="$vuetify.breakpoint.xs ? 3 : 5"
                color="primary"
                circle
                @input="loadStoreProducts()"
              />
            </div>
          </v-card>
        </v-col>

        <!-- ===== BOTTOM LEFT: Daily Report Data ===== -->
        <v-col cols="12" md="6" class="pr-md-3 mb-5 mb-md-0" order="2">
          <v-card rounded="lg" class="pa-4" elevation="1">
            <!-- Date filter row -->
            <div class="d-flex align-center justify-space-between mb-4" style="gap: 12px; flex-wrap: wrap;">
              <h3 class="primary--text font-weight-bold mb-0" style="font-size: 15px;">
                <v-icon color="primary" small class="mr-1">mdi-domain</v-icon>
                Daily Report Data
              </h3>
              <div class="d-flex align-center" style="gap: 8px;">
                <v-menu
                  ref="dayMenu"
                  v-model="dayMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      small
                      outlined
                      color="primary"
                      style="min-width: 140px; text-transform: none;"
                    >
                      <v-icon small left>mdi-calendar-today</v-icon>
                      {{ formatDisplayDate(selectedDay) }}
                    </v-btn>
                  </template>
                  <v-date-picker
                    v-model="selectedDay"
                    no-title
                    scrollable
                    :max="today"
                    @input="
                      dayMenu = false;
                      tablePage = 1;
                      fetchDailyData().then(() => fetchMonthlySummary());
                    "
                  />
                </v-menu>
                <v-btn
                  depressed
                  color="primary"
                  :loading="isLoadingDaily"
                  @click="fetchDailyData().then(() => fetchMonthlySummary())"
                >
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Table -->
            <div class="table-scroll-wrapper">
              <v-data-table
                :headers="tableHeaders"
                :items="dailyRows"
                :loading="isLoadingDaily"
                :page.sync="tablePage"
                :items-per-page="tableItemsPerPage"
                hide-default-footer
                class="report-table"
              >
                <!-- Domain column -->
                <template v-slot:item.storeDetail="{ item }">
                  <span class="font-weight-medium primary--text" style="font-size: 13px;">{{
                    item.primaryDomain || (item.storeDetail && item.storeDetail.defaultSystemDomain)
                  }}</span>
                </template>

                <!-- Rev column -->
                <template v-slot:item.rev="{ item }">
                  <span class="green--text text--darken-2 font-weight-medium">{{ formatCurrency(item.rev) }}</span>
                </template>

                <!-- Spent column - editable -->
                <!-- <template v-slot:item.spent="{ item }">
                  <div class="d-flex align-center" style="gap: 4px;">
                    <template v-if="editingRow !== (item._id || item.id || item.domain)">
                      <span class="red--text text--darken-1 font-weight-medium">{{ formatCurrency(item.spent) }}</span>
                      <v-btn icon small @click="startEdit(item)" class="ml-2 edit-btn">
                        <v-icon size="18" color="grey darken-1">mdi-pencil-outline</v-icon>
                      </v-btn>
                    </template>
                    <template v-else>
                      <v-text-field
                        v-model="editValue"
                        dense
                        hide-details
                        type="number"
                        min="0"
                        step="0.01"
                        prefix="$"
                        style="max-width: 150px; font-size: 14px;"
                        @keyup.enter="confirmEdit(item)"
                        @keyup.esc="cancelEdit"
                        autofocus
                      />
                      <v-btn
                        icon
                        small
                        color="success"
                        :loading="savingRow === item.domain"
                        @click="confirmEdit(item)"
                        class="ml-1 action-btn"
                      >
                        <v-icon size="20">mdi-check-circle</v-icon>
                      </v-btn>
                      <v-btn icon small color="grey darken-1" @click="cancelEdit" class="action-btn">
                        <v-icon size="20">mdi-close-circle</v-icon>
                      </v-btn>
                    </template>
                  </div>
                </template> -->
                <template v-slot:[`item.spent`]="{ item }">
                  <div class="d-flex align-center" style="gap: 8px;">
                    <div v-if="item.details && item.details.length > 0">
                      <div class="d-flex align-center" style="gap: 8px;">
                        <div class="d-flex flex-column" style="gap: 6px;">
                          <div v-for="detail in visibleDetails(item)" :key="detail._id" class="ads-fee-detail">
                            <div v-if="isEditingDetail(detail)">
                              <div class="ads-fee-detail-account" title="Account ID" aria-label="Account ID">
                                {{ detail.accountId }}
                              </div>
                              <div class="d-flex align-center justify-space-between" style="gap: 6px;">
                                <v-text-field
                                  v-model.number="editingDetailValue"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  prefix="$"
                                  dense
                                  hide-details
                                  autofocus
                                  placeholder="0.00"
                                  @keyup.enter="saveEditDetail(detail, item)"
                                  @keyup.esc="cancelEditDetail"
                                  class="custom-form-input-text"
                                />
                                <div class="d-flex align-center" style="gap: 4px;">
                                  <v-btn icon class="custom-btn" @click="cancelEditDetail">
                                    <v-icon size="16">mdi-close</v-icon>
                                  </v-btn>
                                  <v-btn
                                    class="custom-btn"
                                    color="primary"
                                    :loading="loadingEditDetail"
                                    @click="saveEditDetail(detail, item)"
                                  >
                                    <v-icon size="16">mdi-check</v-icon>
                                  </v-btn>
                                </div>
                              </div>
                            </div>

                            <div v-else class="d-flex align-center justify-space-between" style="gap: 12px;">
                              <div class="ads-fee-detail-account" title="Account ID" aria-label="Account ID">
                                {{ detail.accountId }}
                              </div>
                              <div class="d-flex align-center" style="gap: 6px; white-space: nowrap;">
                                <span class="font-weight-medium red--text text--darken-1">{{
                                  formatCurrency(detail.ads_fee)
                                }}</span>
                                <v-btn
                                  class="custom-btn"
                                  icon
                                  color="darken-1"
                                  @click="startEditDetail(detail)"
                                  :disabled="isLoadingDaily || loadingEditDetail"
                                >
                                  <v-icon size="16">mdi-pencil</v-icon>
                                </v-btn>
                              </div>
                            </div>
                          </div>
                        </div>
                        <v-btn icon class="custom-btn" color="darken-1" @click="openCreateDialog(item)">
                          <v-icon size="18">mdi-plus</v-icon>
                        </v-btn>
                      </div>

                      <div v-if="detailsCount(item) > 1" style="margin-left: -4px;" class="mt-1">
                        <v-btn text small color="primary" class="px-1" @click="toggleDetails(item)">
                          {{ isDetailsExpanded(item) ? 'Show less' : `Show more (${detailsCount(item) - 1})` }}
                        </v-btn>
                      </div>
                    </div>
                    <div v-else>
                      <v-btn icon class="custom-btn" color="darken-1" @click="openCreateDialog(item)">
                        <v-icon size="18">mdi-plus</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>

                <!-- ROAS column -->
                <template v-slot:item.roas="{ item }">
                  <span
                    class="font-weight-medium"
                    :class="
                      (item.roas || 0) >= 2
                        ? 'green--text text--darken-2'
                        : (item.roas || 0) >= 1
                        ? 'orange--text text--darken-2'
                        : 'red--text text--darken-1'
                    "
                    >{{ (parseFloat(item.roas) || 0).toFixed(2) }}</span
                  >
                </template>

                <!-- Empty state -->
                <template v-slot:no-data>
                  <div class="text-center py-8 grey--text">
                    <v-icon large color="grey lighten-2">mdi-database-off-outline</v-icon>
                    <div class="mt-2" style="font-size: 13px;">No data for this date</div>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Pagination -->
            <div v-if="dailyRows.length > tableItemsPerPage" class="pagination-bar mt-3 px-1">
              <div class="pagination-bar__info grey--text text--darken-1">
                Showing {{ (tablePage - 1) * tableItemsPerPage + 1 }}–{{
                  Math.min(tablePage * tableItemsPerPage, dailyRows.length)
                }}
                of {{ dailyRows.length }} stores
              </div>
              <div class="pagination-bar__right">
                <div class="d-flex align-center" style="gap: 8px;">
                  <span class="grey--text text--darken-1" style="font-size: 13px; white-space: nowrap;">Rows:</span>
                  <div class="rows-per-page-group">
                    <button
                      v-for="n in [10, 20, 50, 100]"
                      :key="n"
                      class="rows-btn"
                      :class="{ active: tableItemsPerPage === n }"
                      @click="
                        tableItemsPerPage = n;
                        tablePage = 1;
                      "
                    >
                      {{ n }}
                    </button>
                  </div>
                </div>
                <v-pagination
                  v-model="tablePage"
                  :length="Math.ceil(dailyRows.length / tableItemsPerPage)"
                  :total-visible="$vuetify.breakpoint.xs ? 3 : 5"
                  color="primary"
                  circle
                />
              </div>
            </div>

            <!-- Totals summary - responsive -->
            <div class="totals-summary mt-3">
              <div class="totals-summary__label">
                <v-icon small color="grey darken-1" class="mr-1">mdi-sigma</v-icon>
                <span>Total Summary ({{ selectedDay ? formatDisplayDate(selectedDay) : '' }})</span>
              </div>
              <div class="totals-summary__values">
                <div class="totals-chip totals-chip--rev">
                  <span class="totals-chip__label">Revenue</span>
                  <span class="totals-chip__value green--text text--darken-2">{{ formatCurrency(totalRev) }}</span>
                </div>
                <div class="totals-chip totals-chip--spent">
                  <span class="totals-chip__label">Ad Spent</span>
                  <span class="totals-chip__value red--text text--darken-1">{{ formatCurrency(totalSpent) }}</span>
                </div>
                <!-- Profit (tạm ẩn)
                <div class="totals-chip totals-chip--profit">
                  <span class="totals-chip__label">Profit</span>
                  <span
                    class="totals-chip__value font-weight-bold"
                    :class="totalRev - totalSpent >= 0 ? 'green--text text--darken-2' : 'red--text text--darken-1'"
                    >{{ formatCurrency(totalRev - totalSpent) }}</span
                  >
                </div>
                -->
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Create Ads Fee Dialog -->
    <v-dialog v-model="createDialog.show" max-width="500px">
      <v-card class="create-fee-dialog-card">
        <p class="create-fee-dialog-title">Create New Ads Fee</p>
        <v-card-text class="px-4">
          <div>
            <div class="create-fee-dialog-domain" v-if="createDialog.selectedStore">
              <div class="store-image">
                <v-img src="/admin/img/store.png" />
              </div>
              <div>
                {{ getStoreName }}
              </div>
            </div>
          </div>
          <v-form ref="createForm">
            <v-row>
              <v-col cols="12" class="pb-0">
                <label style="font-weight: 700;">Date <span style="color: red">*</span></label>
                <v-menu
                  ref="dateMenuRef"
                  v-model="createDialog.dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="createDialog.date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :rules="[v => !!v || 'Date is required']"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="createDialog.date"
                    :max="new Date().toISOString().substr(0, 10)"
                    no-title
                    scrollable
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="createDialog.dateMenu = false">Cancel</v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="
                        $refs.dateMenuRef.save(createDialog.date);
                        createDialog.dateMenu = false;
                      "
                    >
                      OK
                    </v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" class="form-component pb-0">
                <label>Account <span style="color: red">*</span></label>
                <ReportAvailableAccountSelector
                  v-model="createDialog.accountIdSelected"
                  :rules="[]"
                  :autoFetch="false"
                  :hideDetails="true"
                  class="mb-2"
                  @input="onAccountSelected"
                />
                <div class="text-center mt-0 mb-2 text--secondary">or</div>
                <v-text-field
                  v-model="createDialog.accountIdText"
                  type="text"
                  placeholder="Enter Account ID"
                  hide-details
                  class="custom-form-account"
                  @input="onAccountTextChanged"
                />
                <v-messages :value="createDialog.accountErrors" color="error" />
              </v-col>
              <v-col cols="12" class="form-component pb-0">
                <label>Ads Fee <span style="color: red">*</span></label>
                <v-text-field
                  v-model.number="createDialog.adsFee"
                  type="number"
                  min="0"
                  step="0.01"
                  :rules="[v => v >= 0 || 'Fee must be greater than or equal to 0']"
                  prefix="$"
                  class="custom-form-input-text"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateDialog">Cancel</v-btn>
          <v-btn color="primary" @click="createFeeByAccount" :loading="createDialog.loading">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Inline upload content ads dialog (replaces the dedicated upload screen) -->
    <UpContentAdsDialog
      v-model="uploadDialog.show"
      :productId="uploadDialog.productId"
      :editItem="uploadDialog.editItem"
      @saved="onProductUploadSaved"
    />

    <!-- Ads content detail dialog -->
    <v-dialog v-model="detailDialog.show" max-width="900" scrollable>
      <v-card v-if="detailDialog.prompt" class="detail-dialog-card" rounded="lg">
        <div class="detail-dialog-head">
          <div class="detail-dialog-head__icon">
            <v-icon color="primary">mdi-image-text</v-icon>
          </div>
          <div class="flex-grow-1" style="min-width: 0;">
            <div class="detail-dialog-title text-truncate">{{ detailProductName() }}</div>
            <div class="detail-dialog-meta">
              <v-icon size="13" color="grey">mdi-clock-outline</v-icon>
              <span>{{ formatDateTime(detailDialog.prompt.createdAt) }}</span>
            </div>
          </div>
          <v-btn icon @click="closeDetailDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <v-card-text class="detail-dialog-body pa-4">
          <!-- Attachments -->
          <div v-if="detailDialog.prompt.resource_input && detailDialog.prompt.resource_input.length" class="mb-4">
            <div class="detail-section-label mb-2">
              <v-icon size="16" color="primary" class="mr-1">mdi-paperclip</v-icon>
              Attachments ({{ detailDialog.prompt.resource_input.length }})
            </div>
            <div class="detail-attachments">
              <a
                v-for="(url, i) in detailDialog.prompt.resource_input"
                :key="i"
                :href="imageHelpers.url(url)"
                target="_blank"
                rel="noopener noreferrer"
                class="detail-attachment-item"
              >
                <video v-if="isVideo(url)" :src="imageHelpers.url(url)" class="detail-attachment-media" muted />
                <v-img
                  v-else
                  :src="imageHelpers.url(url, { width: 200, height: 200 })"
                  class="detail-attachment-media"
                  contain
                />
              </a>
            </div>
          </div>

          <!-- Content -->
          <div>
            <div class="detail-section-label mb-2 d-flex align-center justify-space-between">
              <span>
                <v-icon size="16" color="primary" class="mr-1">mdi-text-box-outline</v-icon>
                Content
              </span>
              <v-btn
                small
                outlined
                color="primary"
                :class="{ 'detail-copy-btn--copied': isCopied(detailDialog.prompt.prompt) }"
                style="text-transform: none;"
                @click="copyContent(detailDialog.prompt.prompt)"
              >
                <v-icon size="16" left>
                  {{ isCopied(detailDialog.prompt.prompt) ? 'mdi-check' : 'mdi-content-copy' }}
                </v-icon>
                {{ isCopied(detailDialog.prompt.prompt) ? 'Copied' : 'Copy' }}
              </v-btn>
            </div>
            <div class="detail-content-box">
              {{ detailDialog.prompt.prompt || 'No content' }}
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text @click="closeDetailDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { reportApi } from '@/apis/report';
import { userService } from '@/apis/user.s';
import { productApi } from '@/apis/product';
import storeRequest from '@/apis/request/storeRequest';
import STORAGE_NAME from '@/const/storage';
import moment from 'moment';
import imageHelpers from '@/helpers/image';
import ReportAvailableAccountSelector from './ReportAvailableAccountSelector.vue';
import UpContentAdsDialog from '@/views/website/products/components/UpContentAdsDialog.vue';

export default {
  name: 'ReportPage',
  components: {
    ReportAvailableAccountSelector,
    UpContentAdsDialog,
  },
  data() {
    const today = new Date().toISOString().slice(0, 10);
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const firstDayOfMonth = today.slice(0, 7) + '-01';
    return {
      today,
      // Monthly summary
      monthMenu: false,
      selectedMonth: today.slice(0, 7),
      isLoadingMonthly: false,
      summary: {
        rev: 0,
        spent: 0,
        sales: 0,
        roas: 0,
      },

      // Daily data
      dayMenu: false,
      selectedDay: today,
      isLoadingDaily: false,
      dailyRows: [],

      // Edit state
      editingRow: null,
      editValue: '',
      savingRow: null,

      // Pagination
      tablePage: 1,
      tableItemsPerPage: 20,

      tableHeaders: [
        { text: 'Domain', value: 'storeDetail', sortable: false },
        { text: 'ROAS', value: 'roas', sortable: true, width: '100px' },
        { text: 'Revenue', value: 'rev', sortable: true, width: '150px' },
        { text: 'Ad Spent', value: 'spent', sortable: false, width: '220px' },
      ],

      // Share prompt list
      imageHelpers,
      spStartMenu: false,
      spEndMenu: false,
      sharedPrompt: {
        startDate: yesterday,
        endDate: today,
        page: 1,
        limit: 20,
        total: 0,
        totalPage: 1,
        isLoading: false,
        list: [],
      },
      sharedPromptHeaders: [
        { text: 'Product Name', value: 'productName', sortable: false, width: '24%' },
        { text: 'Attachments', value: 'resource_input', sortable: false, width: '18%' },
        { text: 'Content', value: 'prompt', sortable: false, width: '30%' },
        { text: 'Created At', value: 'createdAt', sortable: false, width: '16%' },
        { text: 'Actions', value: 'actions', sortable: false, width: '12%', align: 'end' },
      ],

      detailsExpandedKeySet: {},
      loadingEditDetail: false,
      editingDetailRowKey: null,
      editingDetailValue: null,

      createDialog: {
        show: false,
        loading: false,
        dateMenu: false,
        date: null,
        adsFee: 0,
        accountIdSelected: null,
        accountIdText: '',
        accountErrors: [],
        selectedStore: null,
      },

      // ===== Inline store-picker + product upload =====
      storePicker: {
        menu: false,
        search: '',
        loaded: false,
      },
      stores: [],
      isLoadingStores: false,
      selectedStore: null,
      originalStoreBaseURL: null,
      originalStoreId: null,

      storeProducts: [],
      isLoadingProducts: false,
      productSearch: '',
      productSearchDebounce: null,
      productPage: 1,
      productLimit: 20,
      totalStoreProducts: 0,
      storeProductHeaders: [
        { text: 'Product Name', value: 'title', sortable: false, width: '24%' },
        { text: 'Attachments', value: 'attachments', sortable: false, width: '16%' },
        { text: 'Content', value: 'content', sortable: false, width: '28%' },
        { text: 'Created At', value: 'createdAt', sortable: false, width: '12%' },
        { text: 'Actions', value: 'action', sortable: false, width: '20%', align: 'end' },
      ],
      productPromptCache: {},

      uploadDialog: {
        show: false,
        productId: null,
        editItem: null,
      },

      detailDialog: {
        show: false,
        prompt: null,
        product: null,
      },
      copiedText: '',
      copiedTimeout: null,
    };
  },
  created() {
    this.originalStoreBaseURL = storeRequest.api?.defaults?.baseURL || null;
    this.originalStoreId = localStorage.getItem(STORAGE_NAME.STORE_ID) || null;
    this.fetchDailyData().then(() => this.fetchMonthlySummary());
    this.fetchSharedPrompts();
  },
  beforeDestroy() {
    if (this.originalStoreBaseURL) {
      storeRequest.setBaseURL(this.originalStoreBaseURL);
    }
    if (this.originalStoreId) {
      storeRequest.setStoreId(this.originalStoreId);
    }
  },
  computed: {
    monthLabel() {
      if (!this.selectedMonth) return 'Select month';
      const [year, month] = this.selectedMonth.split('-');
      return new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
    },
    periodLabel() {
      if (!this.selectedMonth) return '';
      const [year, month] = this.selectedMonth.split('-');
      const lastDay = new Date(year, month, 0).getDate();
      return `${parseInt(month)}/${1}/${year} – ${parseInt(month)}/${lastDay}/${year}`;
    },
    roasClass() {
      const r = this.summary.roas;
      if (r >= 2) return 'metric-roas-good';
      if (r >= 1) return 'metric-roas-ok';
      return 'metric-roas-bad';
    },
    roasColorClass() {
      const r = this.summary.roas;
      if (r >= 2) return 'green--text text--darken-2';
      if (r >= 1) return 'orange--text text--darken-2';
      return 'red--text text--darken-1';
    },
    totalRev() {
      return this.dailyRows.reduce((s, r) => s + (parseFloat(r.rev) || 0), 0);
    },
    totalSpent() {
      return this.dailyRows.reduce((s, r) => s + (parseFloat(r.spent) || 0), 0);
    },
    nextDayLabel() {
      if (!this.selectedDay) return 'next day';
      const d = new Date(this.selectedDay);
      d.setDate(d.getDate() + 1);
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    },
    createAccountId() {
      const selected = (this.createDialog.accountIdSelected || '').trim();
      if (selected) return selected;

      const typed = (this.createDialog.accountIdText || '').trim();
      return typed || null;
    },
    getStoreName() {
      const primaryDomain = this.createDialog.selectedStore?.primaryDomain || '';
      const defaultSystemDomain = this.createDialog.selectedStore?.storeDetail?.defaultSystemDomain || '';
      return primaryDomain || defaultSystemDomain || '';
    },
    filteredStores() {
      const list = (this.stores || []).filter(s => !!s);
      const q = (this.storePicker.search || '').toLowerCase().trim();
      if (!q) return list;
      return list.filter(s => {
        const name = s?.storeDetail?.name?.toLowerCase() || '';
        const domain = s?.storeDetail?.defaultSystemDomain?.toLowerCase() || '';
        return name.includes(q) || domain.includes(q);
      });
    },
    storeProductsTotalPage() {
      return Math.max(1, Math.ceil((this.totalStoreProducts || 0) / this.productLimit));
    },
  },
  methods: {
    formatCurrency(val) {
      if (val === null || val === undefined) return '$0.00';
      return '$' + parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDisplayDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    },
    formatDisplayDateCompact(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    formatDateTime(val) {
      if (!val) return '—';
      const d = new Date(val);
      if (isNaN(d.getTime())) return '—';
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${String(d.getFullYear()).slice(-2)}`;
    },
    formatDate(val) {
      if (!val) return '—';
      const d = new Date(val);
      if (isNaN(d.getTime())) return '—';
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    },
    getProductName(item) {
      if (!item) return '—';
      const p = item.product || item.product_id || item.productDetail || item.product_detail || item.productInfo;
      if (p && typeof p === 'object') return p.title || p.name || '—';
      return item.product_title || item.productName || item.title || '—';
    },
    isVideo(url) {
      if (!url) return false;
      const ext = url
        .split('?')[0]
        .split('.')
        .pop()
        .toLowerCase();
      return ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(ext);
    },

    async fetchMonthlySummary() {
      this.isLoadingMonthly = true;
      try {
        const date =
          this.selectedDay && this.selectedDay.startsWith(this.selectedMonth)
            ? this.selectedDay
            : this.selectedMonth + '-01';
        const { data } = await reportApi.getBySeller(date);
        const byMonth = data?.summary?.byMonth?.[0];
        this.summary = {
          rev: byMonth?.rev ?? 0,
          spent: byMonth?.spent ?? 0,
          sales: byMonth?.order ?? 0,
          roas: byMonth?.roas ?? 0,
        };
      } catch (e) {
        console.error('fetchMonthlySummary', e);
      } finally {
        this.isLoadingMonthly = false;
      }
    },

    async fetchDailyData() {
      this.isLoadingDaily = true;
      try {
        const { data } = await reportApi.getStoresByDate(this.selectedDay);
        this.dailyRows = Array.isArray(data?.store) ? data.store : Array.isArray(data) ? data : [];
      } catch (e) {
        console.error('fetchDailyData', e);
      } finally {
        this.isLoadingDaily = false;
      }
    },

    startEdit(item) {
      this.editingRow = item._id ?? item.id ?? item.domain;
      this.editValue = item.spent;
    },
    cancelEdit() {
      this.editingRow = null;
      this.editValue = '';
    },
    async confirmEdit(item) {
      const newVal = parseFloat(this.editValue);
      if (isNaN(newVal) || newVal < 0) return;
      const rowKey = item._id ?? item.id ?? item.domain;
      this.savingRow = rowKey;
      try {
        await reportApi.updateRow({
          date: this.selectedDay,
          store_id: item._id ?? item.id,
          rev: item.rev,
          order: item.order ?? 0,
          spent: newVal,
        });
        item.spent = newVal;
        this.cancelEdit();
        await Promise.all([this.fetchDailyData(), this.fetchMonthlySummary()]);
      } catch (e) {
        console.error('confirmEdit', e);
      } finally {
        this.savingRow = null;
      }
    },

    async fetchSharedPrompts() {
      this.sharedPrompt.isLoading = true;
      try {
        const { data } = await reportApi.getSharedPromptBySeller({
          startDate: this.sharedPrompt.startDate,
          endDate: this.sharedPrompt.endDate,
          page: this.sharedPrompt.page,
          limit: this.sharedPrompt.limit,
        });
        this.sharedPrompt.list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const p = data?.pagination || {};
        this.sharedPrompt.total = p.total || 0;
        this.sharedPrompt.totalPage = p.totalPage || 1;
      } catch (e) {
        console.error('fetchSharedPrompts', e);
        this.sharedPrompt.list = [];
        this.sharedPrompt.total = 0;
        this.sharedPrompt.totalPage = 1;
      } finally {
        this.sharedPrompt.isLoading = false;
      }
    },

    detailsKey(item) {
      return item?._id || JSON.stringify(item);
    },

    detailsCount(item) {
      return Array.isArray(item?.details) ? item.details.length : 0;
    },

    toggleDetails(item) {
      const key = this.detailsKey(item);
      this.$set(this.detailsExpandedKeySet, key, !this.detailsExpandedKeySet[key]);
    },

    isDetailsExpanded(item) {
      const key = this.detailsKey(item);
      return !!this.detailsExpandedKeySet[key];
    },

    visibleDetails(item) {
      const details = Array.isArray(item?.details) ? item.details : [];
      if (details.length <= 1) return details;
      return this.isDetailsExpanded(item) ? details : details.slice(0, 1);
    },

    getDetailRowKey(detail) {
      if (detail && detail._id) return `id:${detail._id}`;
      return null;
    },

    isEditingDetail(detail) {
      return this.editingDetailRowKey === this.getDetailRowKey(detail);
    },

    cancelEditDetail() {
      this.editingDetailRowKey = null;
      this.editingDetailValue = null;
    },

    startEditDetail(detail) {
      this.editingDetailRowKey = this.getDetailRowKey(detail);
      this.editingDetailValue = detail.ads_fee;
    },

    async saveEditDetail(detail, item) {
      try {
        this.loadingEditDetail = true;
        const params = {
          adsFee: String(this.editingDetailValue),
          date: detail.date,
          accountId: detail.accountId,
          storeId: item._id,
        };
        await reportApi.addFeeDetailByStoreV2(params);
        detail.ads_fee = this.editingDetailValue;
        this.cancelEditDetail();
        await Promise.all([this.fetchDailyData(), this.fetchMonthlySummary()]);
        this.$store.commit('setMessages', {
          messages: 'Ads fee updated successfully!',
          type: 'success',
        });
      } catch (error) {
        console.error('Error saving ads fee:', error);
        this.$store.commit('setMessages', {
          messages: error.response?.data?.message || 'Failed to update ads fee',
          type: 'error',
        });
      } finally {
        this.loadingEditDetail = false;
      }
    },

    onAccountSelected(next) {
      this.createDialog.accountErrors = [];
      const hasSelected = !!(next || '').toString().trim();
      if (hasSelected) {
        this.createDialog.accountIdText = '';
      }
    },

    onAccountTextChanged(next) {
      this.createDialog.accountErrors = [];
      const raw = (next || '').toString();
      const sanitized = raw.replace(/[^a-zA-Z0-9]/g, '');
      if (raw !== sanitized) {
        this.createDialog.accountIdText = sanitized;
        this.createDialog.accountErrors = ['Account ID must contain only letters and numbers'];
      }

      const hasTyped = !!sanitized.trim();
      if (hasTyped) {
        this.createDialog.accountIdSelected = null;
      }
    },

    openCreateDialog(item) {
      this.createDialog.show = true;
      this.createDialog.date = this.selectedDay || moment().format('YYYY-MM-DD');
      this.createDialog.adsFee = 0;
      this.createDialog.accountIdSelected = null;
      this.createDialog.accountIdText = '';
      this.createDialog.accountErrors = [];
      this.createDialog.selectedStore = item;
    },

    closeCreateDialog() {
      this.createDialog.show = false;
      this.createDialog.date = null;
      this.createDialog.adsFee = 0;
      this.createDialog.accountIdSelected = null;
      this.createDialog.accountIdText = '';
      this.createDialog.accountErrors = [];
      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation();
      }
      this.createDialog.selectedStore = null;
    },

    onStorePickerToggle(open) {
      if (open && !this.storePicker.loaded && !this.isLoadingStores) {
        this.fetchStoresForPicker();
      }
    },

    async fetchStoresForPicker() {
      this.isLoadingStores = true;
      this.stores = [];
      try {
        let lastId = null;
        do {
          const { data } = await userService.getStores(lastId ? lastId : '');
          const newStores = (data || [])
            .slice()
            .reverse()
            .filter(store => store !== null);
          this.stores = [...this.stores, ...newStores];
          lastId = newStores?.[0]?.lastId || null;
          if (lastId) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        } while (lastId);
        this.storePicker.loaded = true;
      } catch (err) {
        console.error('fetchStoresForPicker error:', err);
      } finally {
        this.isLoadingStores = false;
      }
    },

    switchToStore(store) {
      const domain = store?.storeDetail?.defaultSystemDomain;
      if (!domain) return false;
      storeRequest.setBaseURL('https://' + domain + '/api/');
      const token = localStorage.getItem(STORAGE_NAME.ACCESS_TOKEN);
      if (token) storeRequest.setToken(token);
      storeRequest.setStoreId(store.id);
      return true;
    },

    onPickStore(store) {
      if (!store.isEnabled) {
        this.$store?.commit?.('setMessages', {
          messages: 'This store is currently disabled.',
          type: 'error',
        });
        return;
      }
      if (this.selectedStore && this.selectedStore.id === store.id) {
        this.storePicker.menu = false;
        return;
      }
      this.selectedStore = store;
      this.storePicker.menu = false;
      this.storeProducts = [];
      this.totalStoreProducts = 0;
      this.productPage = 1;
      this.productSearch = '';
      this.productPromptCache = {};
      if (!this.switchToStore(store)) {
        this.$store?.commit?.('setMessages', {
          messages: 'Cannot resolve this store domain.',
          type: 'error',
        });
        return;
      }
      this.loadStoreProducts();
    },

    clearSelectedStore() {
      this.selectedStore = null;
      this.storePicker.menu = false;
      this.storeProducts = [];
      this.totalStoreProducts = 0;
      this.productPage = 1;
      this.productSearch = '';
      this.productPromptCache = {};
      if (this.originalStoreBaseURL) {
        storeRequest.setBaseURL(this.originalStoreBaseURL);
      }
      if (this.originalStoreId) {
        storeRequest.setStoreId(this.originalStoreId);
      }
    },

    onProductSearch() {
      if (this.productSearchDebounce) clearTimeout(this.productSearchDebounce);
      this.productSearchDebounce = setTimeout(() => {
        this.productPage = 1;
        this.loadStoreProducts();
      }, 350);
    },

    async loadStoreProducts(forceFromFirst = false) {
      if (!this.selectedStore) return;
      if (forceFromFirst) this.productPage = 1;
      this.isLoadingProducts = true;
      try {
        this.switchToStore(this.selectedStore);
        const params = {
          page: this.productPage,
          limit: this.productLimit,
        };
        const search = (this.productSearch || '').trim();
        if (search) params.search = search;
        const [listRes, countRes] = await Promise.all([
          productApi.get(params),
          productApi.count(search ? { search } : {}).catch(() => null),
        ]);
        this.storeProducts = Array.isArray(listRes?.data?.products) ? listRes.data.products : [];
        const c = countRes?.data?.count;
        this.totalStoreProducts = typeof c === 'number' ? c : this.storeProducts.length;
        this.fetchLatestPromptsForVisibleProducts();
      } catch (err) {
        console.error('loadStoreProducts error:', err);
        this.storeProducts = [];
        this.totalStoreProducts = 0;
      } finally {
        this.isLoadingProducts = false;
      }
    },

    productKey(product) {
      return product?._id || product?.id || null;
    },

    getProductImage(product) {
      if (!product) return null;
      const src =
        (product.image && (product.image.src || product.image)) ||
        (Array.isArray(product.images) && product.images[0] && (product.images[0].src || product.images[0])) ||
        null;
      if (!src) return null;
      try {
        return imageHelpers.url(src, { width: 80, height: 80 });
      } catch (e) {
        return src;
      }
    },

    fetchLatestPromptsForVisibleProducts() {
      const ids = this.storeProducts.map(p => this.productKey(p)).filter(Boolean);
      ids.forEach(id => this.fetchLatestPrompt(id));
    },

    async fetchLatestPrompt(productId) {
      if (!productId) return;
      this.$set(this.productPromptCache, productId, { loading: true, latest: null });
      try {
        if (this.selectedStore) this.switchToStore(this.selectedStore);
        const res = await productApi.sharePromtListByProduct(productId, { page: 1, limit: 1 });
        const body = res?.data || {};
        const list = Array.isArray(body.data) ? body.data : [];
        this.$set(this.productPromptCache, productId, { loading: false, latest: list[0] || null });
      } catch (err) {
        console.error('fetchLatestPrompt error:', err);
        this.$set(this.productPromptCache, productId, { loading: false, latest: null });
      }
    },

    isProductPromptLoading(product) {
      const id = this.productKey(product);
      if (!id) return false;
      const cached = this.productPromptCache[id];
      return !!(cached && cached.loading);
    },

    getLatestPrompt(product) {
      const id = this.productKey(product);
      if (!id) return null;
      const cached = this.productPromptCache[id];
      return cached ? cached.latest : null;
    },

    openProductUploadDialog(product) {
      if (!product) return;
      if (this.selectedStore) this.switchToStore(this.selectedStore);
      this.uploadDialog.productId = this.productKey(product);
      this.uploadDialog.editItem = null;
      this.uploadDialog.show = true;
    },

    async onProductUploadSaved() {
      this.$store?.commit?.('setMessages', {
        messages: 'Ads content saved successfully!',
        type: 'success',
      });
      const id = this.uploadDialog.productId;
      if (id) await this.fetchLatestPrompt(id);
    },

    openDetailDialog(prompt, product = null) {
      if (!prompt) return;
      this.detailDialog.prompt = prompt;
      this.detailDialog.product = product;
      this.detailDialog.show = true;
    },

    closeDetailDialog() {
      this.detailDialog.show = false;
      this.detailDialog.prompt = null;
      this.detailDialog.product = null;
    },

    detailProductName() {
      const p = this.detailDialog.prompt;
      const prod = this.detailDialog.product;
      if (prod && (prod.title || prod.name)) return prod.title || prod.name;
      return this.getProductName(p);
    },

    async copyContent(text) {
      const value = (text || '').toString();
      if (!value) return;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value);
        } else {
          const ta = document.createElement('textarea');
          ta.value = value;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        this.copiedText = value;
        if (this.copiedTimeout) clearTimeout(this.copiedTimeout);
        this.copiedTimeout = setTimeout(() => {
          this.copiedText = '';
        }, 1500);
        this.$store?.commit?.('setMessages', {
          messages: 'Content copied to clipboard',
          type: 'success',
        });
      } catch (err) {
        console.error('copyContent error:', err);
        this.$store?.commit?.('setMessages', {
          messages: 'Failed to copy content',
          type: 'error',
        });
      }
    },

    isCopied(text) {
      return !!text && this.copiedText === text;
    },

    async createFeeByAccount() {
      const formOk = this.$refs.createForm.validate();
      const selected = (this.createDialog.accountIdSelected || '').trim();
      const typed = (this.createDialog.accountIdText || '').trim();
      const bothFilled = !!selected && !!typed;
      const typedValid = !typed || /^[a-zA-Z0-9]+$/.test(typed);
      const validAccount = !!this.createAccountId && !bothFilled;

      if (bothFilled) {
        this.createDialog.accountErrors = ['Please fill either Account select OR Account text (not both).'];
      } else if (!typedValid) {
        this.createDialog.accountErrors = ['Account ID must contain only letters and numbers'];
      } else {
        this.createDialog.accountErrors = validAccount ? [] : ['Account is required'];
      }

      if (!formOk || !validAccount || !typedValid) {
        return;
      }

      try {
        this.createDialog.loading = true;

        const params = {
          adsFee: String(this.createDialog.adsFee),
          date: this.createDialog.date,
          accountId: this.createAccountId,
          storeId: this.createDialog.selectedStore._id,
        };

        await reportApi.addFeeDetailByStoreV2(params);

        this.$store.commit('setMessages', {
          messages: 'Fee created successfully',
          type: 'success',
        });
        this.closeCreateDialog();
        await Promise.all([this.fetchDailyData(), this.fetchMonthlySummary()]);
      } catch (error) {
        console.error('Error creating fee:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to create fee';
        this.$store.commit('setMessages', {
          messages: errorMessage,
          type: 'error',
        });
      } finally {
        this.createDialog.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.report-page {
  min-height: 100vh;
  background: #f4f6fb;
  display: flex;
  flex-direction: column;
}

.report-header {
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 10;
}

.report-body {
  flex: 1;
  align-items: flex-start;
}

/* Metric cards container — responsive grid */
.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

/* Metric cards */
.metric-card {
  border-left: 4px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  padding: 12px 16px;

  .metric-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #888;
    margin-bottom: 4px;
  }
  .metric-value {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
  }
}

.metric-rev {
  border-color: #2e7d32;
  background: #f1f8f1;
}
.metric-spent {
  border-color: #c62828;
  background: #fef2f2;
}
.metric-sales {
  border-color: #1565c0;
  background: #f0f4ff;
}
.metric-roas-good {
  border-color: #2e7d32;
  background: #f1f8f1;
}
.metric-roas-ok {
  border-color: #e65100;
  background: #fff8f2;
}
.metric-roas-bad {
  border-color: #c62828;
  background: #fef2f2;
}

/* Table */
.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
}

.report-table {
  border-radius: 8px;

  ::v-deep thead th {
    background: #f0f4ff !important;
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #3f51b5 !important;
    padding-top: 14px !important;
    padding-bottom: 14px !important;
  }

  ::v-deep tbody td {
    padding-top: 14px !important;
    padding-bottom: 14px !important;
    font-size: 14px !important;
  }

  ::v-deep tbody tr:hover {
    background: #f8f9ff !important;
  }
}

/* Responsive totals summary */
.totals-summary {
  background: #f0f4ff;
  border-radius: 10px;
  padding: 12px 16px;
  border: 1px solid #c5cae9;

  &__label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #5c6bc0;
    font-weight: 600;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
  }

  &__values {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
}

.totals-chip {
  flex: 1 1 140px;
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888;
    font-weight: 500;
  }
  &__value {
    font-size: 18px;
    font-weight: 700;
  }

  &--rev {
    background: #e8f5e9;
    border-left: 3px solid #2e7d32;
  }
  &--spent {
    background: #fef2f2;
    border-left: 3px solid #c62828;
  }
  &--profit {
    background: #fff8e1;
    border-left: 3px solid #f57f17;
  }
}

.rows-per-page-group {
  display: flex;
  border: 1px solid #c5cae9;
  border-radius: 6px;
  overflow: hidden;

  .rows-btn {
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 500;
    color: #5c6bc0;
    background: #fff;
    border: none;
    border-right: 1px solid #c5cae9;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    line-height: 1.6;

    &:last-child {
      border-right: none;
    }
    &:hover {
      background: #e8eaf6;
    }
    &.active {
      background: #3f51b5;
      color: #fff;
    }
  }
}

.edit-btn {
  opacity: 0.6;
  transition: opacity 0.15s;
  &:hover {
    opacity: 1;
  }
}

/* Share prompt list — fixed layout so the table always fits, no horizontal scroll */
.share-prompt-table {
  ::v-deep .v-data-table__wrapper {
    overflow-x: hidden;
  }

  ::v-deep table {
    table-layout: fixed;
    width: 100%;
  }

  ::v-deep th,
  ::v-deep td {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
}

.cell-ellipsis {
  max-width: 100%;
  font-size: 12px;
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  cursor: default;
}

.cell-clamp {
  max-width: 100%;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: default;
}

.created-cell {
  font-size: 12px;
  white-space: nowrap;
}

.attachment-thumb {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  transition: transform 0.1s, box-shadow 0.1s;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  }
}

.action-btn {
  transition: transform 0.1s;
  &:hover {
    transform: scale(1.15);
  }
}

/* ===== Pagination bar ===== */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;

  &__info {
    font-size: 13px;
    white-space: nowrap;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}

/* ===== Tablet responsive ===== */
@media (max-width: 959px) {
  // Header — slightly tighter on tablet
  .report-header {
    h2 {
      font-size: 17px !important;
    }
  }

  // Section card heading slightly smaller
  .report-body {
    ::v-deep h3 {
      font-size: 14px !important;
    }
  }

  // Filter row — let it take full width below the title so buttons have room
  .filter-row {
    width: 100%;
    justify-content: flex-start;
  }
}

/* ===== Mobile responsive ===== */
@media (max-width: 599px) {
  // Smaller metric value font
  .metric-card {
    padding: 10px 12px;

    .metric-value {
      font-size: 18px;
    }
  }

  // Smaller totals chip value
  .totals-chip__value {
    font-size: 15px;
  }

  // Pagination: rows selector and pagination on separate line
  .pagination-bar {
    &__right {
      width: 100%;
      justify-content: space-between;
    }

    &__info {
      font-size: 12px;
    }
  }

  // Prevent table cells from wrapping on mobile (force horizontal scroll)
  .report-table {
    ::v-deep tbody td,
    ::v-deep thead th {
      white-space: nowrap;
      padding-left: 8px !important;
      padding-right: 8px !important;
      font-size: 13px !important;
    }
  }

  // Inline edit input narrower on mobile
  .report-table ::v-deep .v-text-field {
    max-width: 110px !important;
  }

  // ----- Header -----
  .report-header {
    h2 {
      font-size: 15px !important;
    }
  }

  // ----- Card padding tighter on mobile -----
  .report-body ::v-deep .v-card.pa-4 {
    padding: 12px !important;
  }

  // ----- Section heading -----
  .report-body ::v-deep h3 {
    font-size: 13px !important;
  }

  // ----- Share prompt / store product table: enable horizontal scroll on mobile -----
  // (Override the fixed layout so cells aren't crushed on narrow screens)
  .share-prompt-table {
    ::v-deep .v-data-table__wrapper {
      overflow-x: auto !important;
    }

    ::v-deep table {
      table-layout: auto !important;
      width: max-content !important;
      min-width: 100% !important;
    }

    ::v-deep th,
    ::v-deep td {
      white-space: nowrap;
      padding-left: 8px !important;
      padding-right: 8px !important;
    }

    // Allow the Content cell to wrap (it's the longest), give it a min-width
    ::v-deep td:nth-child(3) {
      white-space: normal;
      min-width: 200px;
      max-width: 240px;
    }

    // Product name column needs to wrap too with a reasonable min-width
    ::v-deep td:nth-child(1) {
      white-space: normal;
      min-width: 140px;
      max-width: 180px;
    }
  }

  // ----- Filter row (date pills + store picker) -----
  .filter-row {
    width: 100%;

    .date-pill,
    .filter-icon-btn,
    .filter-store-btn {
      height: 30px !important;
      font-size: 11px !important;
    }
  }

  // ----- Selected store info bar -----
  .selected-store-bar {
    padding: 6px 10px;
    font-size: 12px;
  }

  // ----- Detail dialog tighter on mobile -----
  .detail-dialog-card {
    .detail-dialog-head {
      padding: 10px 12px;
    }

    .detail-dialog-title {
      font-size: 13.5px;
    }

    .detail-attachments {
      grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)) !important;
    }

    .detail-content-box {
      font-size: 12.5px;
      padding: 10px 12px;
    }
  }

  // ----- Create fee dialog form input scaling -----
  .create-fee-dialog-card {
    .create-fee-dialog-title {
      font-size: 17px;
    }
  }
}

/* ===== Very narrow phones ===== */
@media (max-width: 399px) {
  .report-header {
    h2 {
      font-size: 14px !important;
    }
  }

  .row-action-btn {
    height: 28px !important;
    font-size: 11px !important;
    padding: 0 6px !important;
  }

  // Totals chips stack into 1 column instead of wrapping awkwardly
  .totals-chip {
    flex: 1 1 100%;
  }
}

.custom-btn {
  padding: 0 !important;
  min-width: 28px !important;
  width: 28px !important;
  height: 28px !important;
  border-radius: 50% !important;
}

.ads-fee-detail {
  padding: 6px 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: #fff;
  width: 250px;
}

.ads-fee-detail-account {
  font-weight: 600;
  font-size: 13px;
  word-break: break-all;
  text-align: left;
}

.create-fee-dialog-card {
  .create-fee-dialog-title {
    font-size: 20px;
    padding-top: 12px;
    padding-left: 12px;
    font-weight: 600;
  }

  .create-fee-dialog-domain {
    border-radius: 8px;
    background: color-mix(in srgb, var(--v-primary-base) 10%, transparent);
    color: var(--v-primary-base);
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;

    .store-image {
      width: 20px;
      img {
        width: 100%;
        height: auto;
        object-fit: contain;
      }
    }
  }

  ::v-deep .custom-form-input-text .v-text-field__prefix {
    padding-left: 12px !important;
  }

  ::v-deep .v-messages__wrapper {
    margin-top: 6px !important;
  }
}

/* ===== Compact filter row (date pills + store picker) ===== */
.filter-row {
  .date-pill {
    min-width: 0 !important;
    padding: 0 10px !important;
    height: 32px !important;
    font-size: 12px !important;
    letter-spacing: 0 !important;
  }

  .filter-icon-btn {
    min-width: 32px !important;
    padding: 0 8px !important;
    height: 32px !important;
  }

  .filter-store-btn {
    height: 32px !important;
    padding: 0 10px !important;
    font-size: 12px !important;
    letter-spacing: 0 !important;
  }
}

/* ===== Store picker dropdown ===== */
.store-picker-card {
  max-height: 420px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.store-picker-list {
  max-height: 280px;
  overflow-y: auto;
  padding-top: 0;
  padding-bottom: 4px;
}

.store-picker-item--active {
  background: #e8eaff !important;
}

/* ===== Selected store info bar (inline product mode) ===== */
.selected-store-bar {
  background: #f0f4ff;
  border: 1px solid #c5cae9;
  border-radius: 10px;
  padding: 8px 12px;
  gap: 8px;

  .store-image {
    width: 28px;
    flex-shrink: 0;
    img {
      width: 100%;
      height: auto;
      object-fit: contain;
    }
  }
}

/* ===== Product row thumbnail (store mode) ===== */
.product-row-thumb {
  width: 40px;
  height: 40px;
  background: #f7f8fc;
  border: 1px solid #ececf3;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ===== Row actions (View detail / Upload) ===== */
.row-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
}

.row-action-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 30px !important;
  min-width: 92px !important;
  padding: 0 12px !important;
  border-radius: 6px !important;
  font-size: 12.5px !important;
  font-weight: 500 !important;
  margin: 0 !important;

  ::v-deep .v-btn__content {
    letter-spacing: 0 !important;
  }
}

.row-action-btn--detail {
  background: #f5f7ff !important;
}

@media (max-width: 599px) {
  .row-actions {
    gap: 4px;
  }
  .row-action-btn {
    min-width: 0 !important;
    padding: 0 8px !important;
  }
}

/* ===== Inline content cell + quick copy ===== */
.content-cell {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding-right: 12px;

  .cell-clamp {
    flex: 1 1 auto;
    min-width: 0;
  }

  .copy-inline-btn {
    flex: 0 0 auto;
    margin-right: 4px;
    opacity: 0.55;
    transition: opacity 0.15s, background 0.15s;

    &:hover {
      opacity: 1;
      background: rgba(63, 81, 181, 0.08);
    }
  }
}

/* ===== Detail dialog ===== */
.detail-dialog-card {
  .detail-dialog-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: linear-gradient(135deg, #f0f4ff 0%, #ffffff 100%);
  }

  .detail-dialog-head__icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #e8eaff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .detail-dialog-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.3;
  }

  .detail-dialog-meta {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }

  .detail-dialog-body {
    max-height: 78vh;
  }

  .detail-section-label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #5c6bc0;
    display: flex;
    align-items: center;
  }

  .detail-attachments {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 8px;
  }

  .detail-attachment-item {
    display: block;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    background: #fafafa;
    aspect-ratio: 1;
    transition: transform 0.15s, box-shadow 0.15s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }

  .detail-attachment-media {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .detail-content-box {
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px 16px;
    font-size: 13.5px;
    line-height: 1.6;
    color: #1f2937;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 480px;
    overflow-y: auto;
  }

  .detail-attachments {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }

  .detail-copy-btn--copied {
    background: rgba(76, 175, 80, 0.12);
    border-color: #4caf50 !important;
    color: #2e7d32 !important;
  }
}
</style>
